/* eslint-disable no-unused-expressions */
import {
  useContext, useCallback, useEffect, useState, useRef,
} from 'preact/hooks';
import { Fragment } from 'preact';
import PropTypes from 'prop-types';
import CountryCode from '../country-code/country-code';
import StoreContext from '../helpers/context';
import { updateContact } from '../store/dispatch-actions';
import {
  textValidator,
  phoneNumberValidator,
  passwordValidator,
} from '../helpers/input-validators';
import {
  getContactFields,
  validateFullName,
  validateEmailId,
  checkEmailAvailability,
} from '../helpers/contact-helper';
import { cleanPhone } from '../helpers/common-helper';
import {
  v6Supported,
  AWDSupported,
  isCanadaBrand,
  isSetmoreStartNowPage,
} from '../helpers/brands';
import { PasswordVisible, PasswordHide } from '../app/icons/common-icons';
import { pushCustomEventToDataLayer } from '../helpers/analytics';
import { EMAIL_AVAILABILITY_STATUS, FORM_ERROR_MESSAGES, SIGNUP_TYPES } from '../utils/common-literals';


const fields = getContactFields();

export default function ContactForm({ signupType }) {
  const { state: { contact, recaptcha }, dispatch } = useContext(StoreContext);

  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordVisibility = showPassword ? 'text' : 'password';
  const passwordInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const [previousInputValue, setPreviousInputValue] = useState();

  const inputRefs = {
    email: emailInputRef,
    password: passwordInputRef,
  };

  const updateState = useCallback((field, value) => {
    dispatch(updateContact({ field, value }));
  });

  const onInput = useCallback((event) => {
    const { dataset: { field }, value } = event.target;
    if (field) {
      if (field === 'phoneNumber') {
        let nationalNumber = value;

        if (event.type === 'paste') {
          event.preventDefault();
          nationalNumber = event.clipboardData.getData('text/plain');
        }

        updateState(field, {
          ...contact[field],
          nationalNumber: cleanPhone(nationalNumber),
          valid: undefined,
        });
      } else if (field === 'email') {
        updateState(field, { ...contact[field], value: value.trim(), valid: undefined });
      } else {
        updateState(field, { ...contact[field], value: value.trimStart(), valid: undefined });
      }

      if (field === 'password') {
        setShowPasswordRules(true);
      }
    }
  });

  const onFocus = useCallback((event) => {
    const { dataset: { field } } = event.target;

    setPreviousInputValue(contact[field].value);

    if (!contact[field].valid) {
      updateState(field, { ...contact[field], valid: undefined });
    }

    if (field === 'password') {
      setShowPasswordRules(true);
    }

    if (isSetmoreStartNowPage()) {
      pushCustomEventToDataLayer({
        event: `${field === 'fullName' ? 'full-name' : field}-click`,
      });
    }
  });

  const onBlur = useCallback((event) => {
    const { dataset: { field }, value, parentElement } = event.target;
    const updatedValue = {
      ...contact[field],
      valid: false,
      value,
    };
    let shouldCheckEmailAvailability = false;
    switch (field) {
      case 'fullName': {
        const { isValid, errorMsg } = validateFullName(field, value);
        updatedValue.valid = isValid;
        updatedValue.errorMsg = errorMsg;
      }
        break;
      case 'companyName':
        updatedValue.valid = textValidator(value);
        break;
      case 'phoneNumber':
        updatedValue.valid = (contact.phoneNumber?.isOptional && !value)
          || phoneNumberValidator(value, contact[field].countryCode);
        updatedValue.nationalNumber = value;
        break;
      case 'email': {
        if (previousInputValue && previousInputValue === value && !!contact.email.valid) return;

        let { isValid, errorMsg } = validateEmailId(field, value);

        if (isValid) {
          const emailLookupCache = contact.email.lookupCache?.find(
            lookup => lookup.email === contact.email.value,
          );
          if (!emailLookupCache) {
            shouldCheckEmailAvailability = true;
          } else if (emailLookupCache.status === EMAIL_AVAILABILITY_STATUS.UNAVAILABLE) {
            isValid = false;
            errorMsg = FORM_ERROR_MESSAGES.email.alreadyExists;
          }
        }

        updatedValue.valid = isValid;
        updatedValue.errorMsg = errorMsg;
      }
        break;
      case 'password':
        updatedValue.valid = passwordValidator(value);
        setShowPasswordRules(false);
        break;
      default:
        break;
    }
    if (v6Supported && value) {
      parentElement?.classList.add('has-val');
    } else {
      parentElement?.classList.remove('has-val');
    }

    if (isSetmoreStartNowPage()
      && previousInputValue !== updatedValue.value) {
      pushCustomEventToDataLayer({
        event: `${field === 'fullName' ? 'full-name' : field}-entered`,
      });
    }

    updateState(field, updatedValue);

    if (shouldCheckEmailAvailability) {
      checkEmailAvailability({
        value,
        contact,
        dispatch,
        recaptcha,
        signupType,
      });
    }
  });

  const onCountryCodeChange = useCallback((selectedCountryInfo) => {
    const countryCode = selectedCountryInfo.iso2;
    const country = selectedCountryInfo.code;
    const field = 'phoneNumber';
    const valid = (contact.phoneNumber?.isOptional && !contact[field].nationalNumber)
                  || phoneNumberValidator(contact[field].nationalNumber, countryCode);
    updateState(field, {
      ...contact[field], countryCode, country, valid,
    });
  });

  const onKeyUp = useCallback((event) => {
    if (event?.keyCode !== 13) return;

    setShowPasswordRules(false);
    event.target.blur();

    if (isSetmoreStartNowPage() || signupType === SIGNUP_TYPES.PAID_SIGNUP) return;

    event.preventDefault();
    document.getElementById('submit-btn').click();
  });

  const moveCursorToEnd = (passwordField) => {
    setTimeout(() => {
      const { length } = passwordField.value;
      passwordField.setSelectionRange(length, length);
    }, 0);
  };

  const togglePasswordVisibility = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowPassword(!showPassword);
    passwordInputRef.current.focus();
    moveCursorToEnd(passwordInputRef.current);
  };

  useEffect(() => {
    const email = contact.email.value;
    if (email) {
      if (!contact.email.valid) {
        updateState('email', {
          ...contact.email,
          errorMsg: FORM_ERROR_MESSAGES.email.alreadyExists,
        });
      }
      onBlur({
        target: {
          dataset: {
            field: 'email',
          },
          value: email,
          parentElement: emailInputRef.current,
        },
      });
    }
  }, []);

  return (
    <ul className={`form-field-wrapper ${isCanadaBrand || isSetmoreStartNowPage() ? '' : 'mb-5'} ${v6Supported ? 'v6fields' : ''}`}>
      {Object.keys(fields).map((eachField, index) => (
        !contact[eachField].disabled
          && <li className="form-field" id={eachField} key={eachField}>
            {
              AWDSupported ? <div className={`awd-input-wrapper ${eachField === 'phoneNumber' ? 'g-custom-input-field' : ''} ${contact[eachField].valid === false ? 'error has-desc' : ''} ${contact[eachField].value && eachField === 'email' ? 'has-val' : ''}`}>
                {eachField === 'phoneNumber'
                  ? <Fragment>
                    <input type="tel" tabIndex={index + 1} className="awd-input" data-field={eachField} placeholder={fields[eachField].placeholder} data-testid={eachField} onInput={onInput} onKeyUp={onKeyUp} onPaste={onInput} onBlur={onBlur} onFocus={onFocus} value={contact[eachField].nationalNumber} />
                    <label className="awd-input-label">
                      {fields[eachField].label}
                    </label>
                    <CountryCode
                    selectedDialCode={contact[eachField].countryCode}
                    selectedCountry={contact[eachField].country}
                    onDialCodeChange={onCountryCodeChange}
                    />
                  </Fragment>
                  : <Fragment>
                    <input className="awd-input" type={eachField === 'password' ? 'password' : 'text'} placeholder={fields[eachField].placeholder} tabIndex={index + 1} data-field={eachField} data-testid={eachField} onFocus={onFocus} onInput={onInput} onKeyUp={onKeyUp} onBlur={onBlur} value={contact[eachField].value} ref={inputRefs[eachField] || null} />
                    <label className="awd-input-label">
                      {fields[eachField].label}
                    </label>
                  </Fragment>
                  }
                <div className="awd-input-desc">
                  {contact[eachField].errorMsg}
                </div>
              </div> : <Fragment>
                { v6Supported ? <div className={`g-inputfield-group ${eachField === 'phoneNumber' ? 'g-custom-input-field' : ''} ${contact[eachField].valid === false ? 'error has-desc' : ''} ${contact[eachField].value && eachField === 'email' ? 'has-val' : ''}`}>
                  {eachField === 'phoneNumber'
                    ? <Fragment>
                      <input type="tel" tabIndex={index + 1} className="g-inputfield" data-field={eachField} placeholder={fields[eachField].placeholder} data-testid={eachField} onInput={onInput} onKeyUp={onKeyUp} onPaste={onInput} onBlur={onBlur} onFocus={onFocus} value={contact[eachField].nationalNumber} />
                      <label className="g-inputfield-label">
                        {fields[eachField].label}
                      </label>
                      <CountryCode
                      selectedDialCode={contact[eachField].countryCode}
                      selectedCountry={contact[eachField].country}
                      onDialCodeChange={onCountryCodeChange}
                    />
                    </Fragment>
                    : <Fragment>
                      <input className="g-inputfield" type={eachField === 'password' ? 'password' : 'text'} placeholder={fields[eachField].placeholder} tabIndex={index + 1} data-field={eachField} data-testid={eachField} onFocus={onFocus} onInput={onInput} onKeyUp={onKeyUp} onBlur={onBlur} value={contact[eachField].value} ref={inputRefs[eachField] || null} />
                      <label className="g-inputfield-label">
                        {fields[eachField].label}
                      </label>
                    </Fragment>
                  }
                  <div className="g-inputfield-desc">
                    {contact[eachField].errorMsg}
                  </div>
                </div> : <div className={`g-input-wrapper ${contact[eachField].valid === false ? 'error' : ''}`}>
                  {!isSetmoreStartNowPage() && (<label className="g-input-label" id="inputLabel">{fields[eachField].label}</label>)}
                  {eachField === 'phoneNumber' ? <div className="g-custom-input-field">
                    <input type="tel" tabIndex={index + 1} className="g-input" data-field={eachField} placeholder={fields[eachField].placeholder} data-testid={eachField} onInput={onInput} onKeyUp={onKeyUp} onPaste={onInput} onBlur={onBlur} onFocus={onFocus} value={contact[eachField].nationalNumber} />
                    <CountryCode
                      selectedDialCode={contact[eachField].countryCode}
                      selectedCountry={contact[eachField].country}
                      onDialCodeChange={onCountryCodeChange}
                    />
                  </div>
                    : <>
                      <input
                      tabIndex={index + 1}
                      type={eachField === 'password' ? passwordVisibility : 'text'}
                      className={`g-input ${isSetmoreStartNowPage() ? 'pr-10' : ''}`}
                      data-field={eachField}
                      data-testid={eachField}
                      placeholder={fields[eachField].placeholder}
                      onFocus={onFocus}
                      onInput={onInput}
                      onKeyUp={onKeyUp}
                      onBlur={onBlur}
                      value={contact[eachField].value}
                      ref={inputRefs[eachField] || null}
                    />
                      {(isSetmoreStartNowPage() && eachField === 'password') && (
                        <>
                          <button
                            className="view-password"
                            aria-label={showPassword ? 'Click to hide password' : 'Click to show password'}
                            type="button"
                            data-testId="passwordToggleBtn"
                            onMouseDown={togglePasswordVisibility}>
                            {showPassword ? <PasswordHide /> : <PasswordVisible />}
                          </button>
                          <ul className={`password-input-guide ${showPasswordRules ? '' : 'd-none'}`}>
                            <li className='text text-xs'>Minimum 8 characters long</li>
                            <li className='text text-xs'>Contains both characters and numbers</li>
                          </ul>
                        </>
                      )}
                    </>}
                  <div className="g-input-msg">
                    <span>{contact[eachField].errorMsg}</span>
                  </div>
                </div>
              }
              </Fragment>
            }
          </li>
      ))}
    </ul>
  );
}


ContactForm.propTypes = {
  signupType: PropTypes.string,
};

ContactForm.defaultProps = {
  signupType: SIGNUP_TYPES.PAID_SIGNUP,
};
